import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatBytes, formatDuration, formatInTz } from "@/lib/format";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Camera, Film, FolderKanban, ScanEye, Tag, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";

function StatCard({ title, value, icon: Icon, hint }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tracking-tight">{value}</div>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery({ queryKey: ["stats"], queryFn: api.dashboard.stats });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });

  const projectMap = new Map();
  (projects || []).forEach((p) => projectMap.set(p.id, { name: p.name, timezone: p.timezone }));

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="dashboard-page">
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Tablou de bord</h1>
          <p className="text-sm text-muted-foreground">
            Privire de ansamblu asupra șantierelor, camerelor și filmelor zilnice. Datele sunt afișate în fusul orar al fiecărui proiect.
          </p>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-4">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28" />)
          ) : (
            <>
              <StatCard title="Proiecte active" value={stats?.activeProjects ?? 0} icon={FolderKanban} hint={`${stats?.projects ?? 0} totale`} />
              <StatCard title="Camere" value={stats?.cameras ?? 0} icon={Camera} />
              <StatCard title="Clipuri" value={stats?.clips ?? 0} icon={Video} />
              <StatCard title="Filme zilnice" value={stats?.films ?? 0} icon={Film} />
              <StatCard title="Evenimente" value={stats?.events ?? 0} icon={Tag} />
              <StatCard title="Durată cumulată" value={formatDuration(stats?.totalDurationSec ?? 0)} icon={ScanEye} />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Filme generate recent</CardTitle></CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-32" /> : !stats?.recentFilms?.length ? (
                <p className="text-sm text-muted-foreground">Nu există filme încă. Încărcați o arhivă pentru a genera automat filme zilnice.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {stats.recentFilms.map((f) => {
                    const project = projectMap.get(f.projectId);
                    return (
                      <li key={f.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{f.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {project?.name ?? `Proiect #${f.projectId}`} · {f.filmDate} · {formatDuration(f.durationSec)} · {f.clipsCount} clipuri
                          </p>
                        </div>
                        <Badge variant={f.isAuto ? "secondary" : "default"} className="shrink-0">{f.isAuto ? "AUTO" : "MANUAL"}</Badge>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Clipuri încărcate recent</CardTitle></CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-32" /> : !stats?.recentClips?.length ? (
                <p className="text-sm text-muted-foreground">
                  Nu există clipuri încărcate. Mergeți la <button onClick={() => navigate("/upload")} className="text-primary underline">Încărcare arhive</button> pentru a începe.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {stats.recentClips.map((c) => {
                    const project = projectMap.get(c.projectId);
                    return (
                      <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.filename}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {project?.name ?? `Proiect #${c.projectId}`} · {formatInTz(c.startedAt, project?.timezone)} · {formatBytes(c.sizeBytes)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
